import {
  ClientDomain,
  TwUser,
  toFormData,
  type JsonHelper,
  type Logger,
  type SingleResponse,
} from "../../../tastyworks"
import type { Params } from "../../../tastyworks/request"
import {
  ADMIN_LOGIN_PARSER,
  type AdminTwLogin,
} from "../../../tastyworks/admin/admin-session"
import type HttpClient from "../../../tastyworks/http"
import { InvalidSessionError, toJsonHelper } from "../../../tastyworks/http"
import {
  parseSingleResponse,
  updateSingleResponse,
} from "../../../tastyworks/response"
import { USER_DESER } from "../../../tastyworks/user"

export class AdminSessionService {
  private readonly sessionsClient: HttpClient

  constructor(
    baseHttpClient: HttpClient,
    private readonly logger: Logger
  ) {
    this.sessionsClient = baseHttpClient.nested("sessions")
  }

  readonly login = async (
    login: string,
    password: string,
    clientDomain: ClientDomain = ClientDomain.Customer,
    oneTimePassword?: string
  ): Promise<SingleResponse<AdminTwLogin>> => {
    const url = this.sessionsClient.baseUrl.toUrl("")
    const useCookieAuth = this.sessionsClient.currentTransport === "cookie-csrf"

    const params: Params = { login, password, clientDomain }
    if (useCookieAuth) {
      params["use-cookies"] = true
    }
    const body = toFormData(params)

    // Do a raw fetch request to bypass SessionErrorHandler
    const headers: HeadersInit = {}
    headers["X-Tastyworks-OTP"] = oneTimePassword ?? ""

    const requestInit: RequestInit = { body, headers, method: "POST" }
    if (useCookieAuth) {
      requestInit.credentials = "include"
    }
    const fetchResponse = await this.sessionsClient.fetcher(url, requestInit)

    const helper = await toJsonHelper(fetchResponse)

    const response = parseSingleResponse(helper, ADMIN_LOGIN_PARSER)

    return response
  }

  readonly validate = async (
    sessionToken: string
  ): Promise<SingleResponse<TwUser>> => {
    const useCookieAuth = this.sessionsClient.currentTransport === "cookie-csrf"
    if (!useCookieAuth && !sessionToken) {
      throw new Error("sessionToken required")
    }

    let helper: JsonHelper | null = null
    try {
      helper = await this.sessionsClient.postJson("validate", {})
    } catch (err) {
      if (!(err instanceof InvalidSessionError)) {
        throw err
      }
      helper = await toJsonHelper(err.response)
    }

    const user = new TwUser()
    const response = updateSingleResponse(helper, user, USER_DESER.update)
    if (!useCookieAuth) {
      user.sessionToken = sessionToken
    }

    return response
  }

  readonly logout = async (sessionToken: string): Promise<boolean> => {
    const useCookieAuth = this.sessionsClient.currentTransport === "cookie-csrf"
    if (!useCookieAuth && !sessionToken) {
      return false
    }

    const url = this.sessionsClient.baseUrl.toUrl("")
    try {
      await this.sessionsClient.fetch(url, "DELETE")
      return true
    } catch (error) {
      this.logger.warn("Error on logout", error)
      return false
    }
  }
}
